import React, { useState, useCallback, memo } from 'react';
import { Typography, Box, Button, Chip, TextField, IconButton } from '@mui/material';
import { FindInPage as FindingIcon, ZoomIn as DiveDeepIcon, LocalOffer as TagIcon, Add as AddIcon } from '@mui/icons-material';
import { Finding } from '../../../types';
import ItemViewModal from '../../../shared/components/ItemViewModal';
import MarkdownContent from '../../../shared/components/MarkdownContent';
import CopyIconButton from '../../../shared/components/CopyIconButton';

interface FindingViewModalProps {
  open: boolean;
  onClose: () => void;
  finding: Finding | null;
  onDiveDeeper?: (finding: Finding) => void;
  onUpdateTags?: (findingId: string, tags: string[]) => void;
}

const FindingViewModal: React.FC<FindingViewModalProps> = ({
  open,
  onClose,
  finding,
  onDiveDeeper,
  onUpdateTags,
}) => {
  const [newTag, setNewTag] = useState('');
  const [localTags, setLocalTags] = useState<string[]>(finding?.tags || []);

  React.useEffect(() => {
    setLocalTags(finding?.tags || []);
  }, [finding?.id, finding?.tags]);

  const handleDiveDeeper = () => {
    if (onDiveDeeper) {
      onDiveDeeper(finding!);
      onClose();
    }
  };

  const handleAddTag = useCallback(() => {
    if (!finding || !onUpdateTags || !newTag.trim()) return;
    const newTags = newTag.split(/[,\s]+/).map(t => t.trim()).filter(Boolean);
    const uniqueNew = newTags.filter(t => !localTags.includes(t));
    if (uniqueNew.length === 0) {
      setNewTag('');
      return;
    }
    const updated = [...localTags, ...uniqueNew];
    setLocalTags(updated);
    onUpdateTags(finding.id, updated);
    setNewTag('');
  }, [finding, onUpdateTags, newTag, localTags]);

  const handleRemoveTag = useCallback((tagToRemove: string) => {
    if (!finding || !onUpdateTags) return;
    const updated = localTags.filter(t => t !== tagToRemove);
    setLocalTags(updated);
    onUpdateTags(finding.id, updated);
  }, [finding, onUpdateTags, localTags]);

  if (!finding) return null;

  return (
    <ItemViewModal
      open={open}
      onClose={onClose}
      icon={<FindingIcon />}
      title="Finding Details"
      color="secondary"
      rank={finding.rank}
      actions={
        <>
          <CopyIconButton
            text={`${finding.title}\n\n${finding.description}`}
            tooltip="Copy finding"
            sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
          />
          {onDiveDeeper && (
            <Button
              onClick={handleDiveDeeper}
              variant="outlined"
              startIcon={<DiveDeepIcon />}
            >
              Dive Deeper
            </Button>
          )}
        </>
      }
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Title
        </Typography>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {finding.title}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: onUpdateTags ? 1 : 0 }}>
          {localTags.map(tag => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              icon={<TagIcon sx={{ fontSize: '0.8rem' }} />}
              onDelete={onUpdateTags ? () => handleRemoveTag(tag) : undefined}
            />
          ))}
        </Box>
        {onUpdateTags && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Add tags (comma or space separated)..."
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={handleAddTag}
              disabled={!newTag.trim()}
              color="primary"
            >
              <AddIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Description
        </Typography>
        <MarkdownContent content={finding.description} accentColor="secondary" />
      </Box>

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Finding ID
        </Typography>
        <Typography variant="body2" color="text.primary" sx={{ fontFamily: 'monospace' }}>
          {finding.id}
        </Typography>
      </Box>
    </ItemViewModal>
  );
};

export default memo(FindingViewModal);